/**
 * SettingsSiteAvailability — Iter 191b.
 *
 * Squarespace-style visibility control. Vendor picks one of four modes:
 *   • Public              — anyone can see the site
 *   • Password Protected  — anyone with the site password can see it
 *   • Private             — only contributors can see it (default)
 *   • SSO Protected       — Enterprise-only, locked in UI
 *
 * Persistence:
 *   GET  /api/builder/settings                        → returns { visibility, site_password }
 *   PUT  /api/builder/settings { visibility, site_password }
 *
 * The public site renderer should read `settings.visibility` and gate access
 * accordingly (follow-up ticket).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Search, Lock, Loader2, Eye, EyeOff } from 'lucide-react';
import BackToSettings from './_BackToSettings';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const OPTIONS = [
  { id: 'public',   label: 'Public',             sub: 'Anyone can see the site.' },
  { id: 'password', label: 'Password Protected', sub: 'Anyone with the password can see this site.' },
  { id: 'private',  label: 'Private',            sub: 'Only contributors can see the site.' },
  { id: 'sso',      label: 'SSO Protected',      sub: 'Only authorized single sign-on (SSO) users can see the site.', enterprise: true },
];

function Radio({ option, active, onSelect }) {
  const disabled = option.enterprise;
  return (
    <div
      onClick={() => !disabled && onSelect(option.id)}
      className={`flex items-start gap-4 py-4 pl-1 pr-4 rounded-lg transition-all ${
        disabled ? 'opacity-60' : 'cursor-pointer hover:bg-white/[0.03]'
      }`}
      data-testid={`site-availability-option-${option.id}`}
    >
      <div className="pt-0.5">
        <div
          className={`w-5 h-5 rounded-full border-2 grid place-items-center transition-colors ${
            active ? 'border-[#A78BFA]' : 'border-white/25'
          }`}
        >
          {active && <div className="w-2.5 h-2.5 rounded-full bg-[#A78BFA]" />}
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-[14px] font-semibold text-white">{option.label}</span>
          {option.enterprise && (
            <span className="inline-flex items-center gap-1 px-2 py-[3px] rounded-md text-[9px] font-bold uppercase tracking-[0.14em] bg-[#F59E0B]/15 text-[#FBBF24] border border-[#F59E0B]/25">
              <Lock size={9} /> Enterprise
            </span>
          )}
        </div>
        <p className="mt-1 text-[12.5px] text-[#9CA3AF] leading-snug">{option.sub}</p>
      </div>
    </div>
  );
}

export default function SettingsSiteAvailability() {
  const [visibility, setVisibility] = useState('private');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem('token');

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/builder/settings`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setVisibility(res.data?.visibility || 'private');
        setPassword(res.data?.site_password || '');
      } catch (_) { /* silent */ }
      finally { setLoading(false); }
    })();
  }, [token]);

  const persist = async (patch) => {
    setSaving(true);
    try {
      await axios.put(`${API}/builder/settings`, patch, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch { toast.error('Could not save'); }
    finally { setSaving(false); }
  };

  const selectVisibility = (id) => {
    if (id === 'sso') return; // enterprise-locked
    setVisibility(id);
    persist({ visibility: id });
    toast.success(`Set to ${OPTIONS.find(o => o.id === id)?.label}`);
  };

  const commitPassword = () => {
    persist({ site_password: password });
    toast.success('Password saved');
  };

  return (
    <div className="min-h-full" data-testid="settings-site-availability-page">
      {/* Top rail */}
      <div className="border-b border-white/[0.06] bg-white/[0.02] backdrop-blur-xl px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BackToSettings />
          <div className="w-px h-4 bg-white/10" />
          <h1 className="text-[13px] font-bold text-white uppercase tracking-[0.18em]">Settings</h1>
        </div>
        <div className="flex items-center gap-2">
          <button className="w-8 h-8 grid place-items-center text-white/60 hover:text-white hover:bg-white/[0.06] rounded-md transition-all" title="Search settings">
            <Search size={14} />
          </button>
          {saving && (
            <span className="text-[10px] uppercase tracking-[0.14em] text-[#A78BFA] font-bold animate-pulse">
              Saving…
            </span>
          )}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-8 py-10">
        <div className="text-[12px] text-[#64748B] mb-3">
          <span className="text-white/70 font-semibold">Website</span>
          <span className="mx-2">/</span>
          <span className="text-white">Site Availability</span>
        </div>

        <h2 className="text-[32px] font-bold text-white tracking-tight" style={{ fontFamily: 'Syne, Inter, sans-serif' }}>
          Site Availability
        </h2>
        <p className="mt-3 text-[14px] text-[#9CA3AF] leading-relaxed max-w-2xl">
          Control who your site is available to.
        </p>

        {/* Upgrade CTA (mirrors Squarespace's "Upgrade to publish" chip) */}
        <div className="mt-8">
          <button
            type="button"
            className="px-5 py-2.5 rounded-md bg-white text-[#0E0E23] text-[11px] font-bold uppercase tracking-[0.14em] hover:bg-[#F5F5F5] transition-all shadow-[0_0_24px_rgba(255,255,255,0.06)]"
            data-testid="upgrade-to-publish-btn"
          >
            Upgrade to Publish
          </button>
        </div>

        {loading ? (
          <div className="mt-10 flex items-center gap-2 text-white/50">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        ) : (
          <section className="mt-8 rounded-xl bg-white/[0.03] border border-white/[0.08] p-6">
            <div className="space-y-1">
              {OPTIONS.map((o) => (
                <Radio key={o.id} option={o} active={visibility === o.id} onSelect={selectVisibility} />
              ))}
            </div>

            {/* Password field — only when Password Protected is selected */}
            {visibility === 'password' && (
              <div
                className="mt-5 ml-9 p-4 rounded-lg bg-white/[0.03] border border-white/[0.08] max-w-md"
                data-testid="site-password-panel"
              >
                <label className="text-[10.5px] uppercase tracking-[0.14em] text-[#64748B] font-bold">
                  Site Password
                </label>
                <div className="mt-2 flex items-center gap-2">
                  <div className="flex-1 relative">
                    <input
                      type={showPw ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onBlur={commitPassword}
                      placeholder="e.g. entreprise-2026"
                      className="w-full bg-[#0A0A1F] border border-white/10 rounded-md px-3 py-2 pr-9 text-[13px] text-white placeholder-white/25 focus:outline-none focus:border-[#A78BFA]/60"
                      data-testid="site-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw((v) => !v)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 grid place-items-center text-white/50 hover:text-white rounded"
                    >
                      {showPw ? <EyeOff size={13} /> : <Eye size={13} />}
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={commitPassword}
                    className="px-3 py-2 rounded-md bg-[#A78BFA] text-[#0E0E23] text-[11px] font-bold uppercase tracking-wider hover:bg-white transition-all"
                    data-testid="site-password-save"
                  >
                    Save
                  </button>
                </div>
                <p className="mt-2 text-[11px] text-[#64748B]">
                  Visitors will need this password to view your site. Share it privately with people you want to give access to.
                </p>
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
}
